import { yupResolver } from "@hookform/resolvers/yup";
import { addAction } from "@wayflyer/blackbox-fe";
import type { Item, RadioOption } from "@wayflyer/flyui";
import { FormLayout } from "@wayflyer/flyui";
import { RhfDropdownField, RhfRadioField } from "@wayflyer/flyui-hook-form";
import type { FundingApplicationSchemas } from "api-types";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useErrorModal } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";

import { useFundingApplication, useFundingApplicationMutation } from "../../queries/funding-application/queries";
import { COMMON_MESSAGES } from "../common-messages";
import { CurrencyInput } from "../currency-input/currency-input";
import { RSS_GEOS } from "../constants";

import { MESSAGES } from "./message-definitions";

type BusinessModelChoice =
  | "ecommerce_dtc_or_wholesale"
  | "retail"
  | "saas_or_software"
  | "service_provider"
  | "other_rss";

// Should match CustomerFundingApplicationUpdate and CustomerFundingApplicationData section
export type RevenueFormFields = {
  reportedRevenueCurrency: string;
  reportedRevenue: number;
  hasRetail: boolean;
  businessModel?: BusinessModelChoice | null;
  retailPercentage?: string;
  hasDropship: boolean;
  dropshipPercentage?: string;
};

const PERCENTAGE_OPTIONS: Item<string>[] = [
  { id: "under_twenty", text: "0-20%" },
  { id: "twenty_one_to_forty", text: "21-40%" },
  { id: "forty_one_to_sixty", text: "41-60%" },
  { id: "sixty_one_to_eighty", text: "61-80%" },
  { id: "over_eighty", text: "81-100%" },
];

type RevenueEntryFormProps = {
  onSubmit: (formData: RevenueFormFields) => Promise<void>;
  initialValues: RevenueFormFields;
  isRssApplicationFlowEnabled?: boolean;
  formId: string;
};

const RevenueEntryForm = ({ onSubmit, initialValues, isRssApplicationFlowEnabled, formId }: RevenueEntryFormProps) => {
  const intl = useIntl();

  const BUSINESS_MODEL_OPTIONS: RadioOption[] = [
    {
      value: "ecommerce_dtc_or_wholesale",
      label: intl.formatMessage(MESSAGES.businessModelEcommerce),
    },
    {
      value: "retail",
      label: intl.formatMessage(MESSAGES.businessModelRetail),
    },
    {
      value: "saas_or_software",
      label: intl.formatMessage(MESSAGES.businessModelSaas),
    },
    {
      value: "service_provider",
      label: intl.formatMessage(MESSAGES.businessModelService),
    },
    {
      value: "other_rss",
      label: intl.formatMessage(MESSAGES.businessModelOther),
    },
  ];

  const schema = Yup.object().shape({
    reportedRevenueCurrency: Yup.string().trim().required(),
    reportedRevenue: Yup.number()
      .typeError(intl.formatMessage(MESSAGES.revenueAmountError))
      .positive(intl.formatMessage(MESSAGES.revenueAmountInvalidError))
      .required(intl.formatMessage(MESSAGES.revenueAmountError)),
    hasRetail: isRssApplicationFlowEnabled
      ? Yup.bool()
      : Yup.bool().required(intl.formatMessage(MESSAGES.hasRetailError)),
    businessModel: isRssApplicationFlowEnabled
      ? Yup.string().required(intl.formatMessage(MESSAGES.businessModelError))
      : Yup.string().nullable(),
    retailPercentage: isRssApplicationFlowEnabled
      ? Yup.mixed().when("businessModel", ([businessModel], schema) =>
          businessModel === "ecommerce_dtc_or_wholesale"
            ? Yup.string().required(intl.formatMessage(MESSAGES.percentageError))
            : schema
        )
      : Yup.mixed().when("hasRetail", ([hasRetail], schema) =>
          hasRetail ? Yup.string().required(intl.formatMessage(MESSAGES.percentageError)) : schema
        ),
    hasDropship: isRssApplicationFlowEnabled
      ? Yup.mixed().when("businessModel", ([businessModel], schema) =>
          businessModel === "ecommerce_dtc_or_wholesale"
            ? Yup.bool().required(intl.formatMessage(MESSAGES.hasDropshipError))
            : schema
        )
      : Yup.bool().required(intl.formatMessage(MESSAGES.hasDropshipError)),
    dropshipPercentage: Yup.mixed().when("hasDropship", ([hasDropship], schema) =>
      hasDropship ? Yup.string().required(intl.formatMessage(MESSAGES.percentageError)) : schema
    ),
  });

  const form = useForm<RevenueFormFields>({
    resolver: yupResolver(schema),
    defaultValues: initialValues,
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const hasRetail = form.watch("hasRetail");
  const businessModel = form.watch("businessModel");
  const hasDropship = form.watch("hasDropship");

  const {
    formState: { errors },
  } = form;

  const onRevenueSubmit = async (formData: RevenueFormFields) => {
    await onSubmit(formData);
  };

  return (
    <form id={formId} data-testid="revenue-form" onSubmit={form.handleSubmit(onRevenueSubmit)}>
      <FormLayout>
        <CurrencyInput
          name="revenue-amount"
          label={intl.formatMessage(MESSAGES.revenueLabel)}
          onChange={({ unitId, unitValue }) => {
            form.setValue("reportedRevenueCurrency", unitId);
            form.setValue("reportedRevenue", unitValue);
          }}
          error={errors.reportedRevenue?.message}
          defaultValue={initialValues?.reportedRevenue}
          defaultCurrency={initialValues?.reportedRevenueCurrency}
        />
        {isRssApplicationFlowEnabled ? (
          <RhfRadioField
            form={form}
            name="businessModel"
            type="block"
            label={intl.formatMessage(MESSAGES.businessModelLabel)}
            defaultValue={initialValues?.businessModel}
            onChange={(value) => {
              if (value === "ecommerce_dtc_or_wholesale") {
                form.setValue("hasRetail", true);
              } else {
                form.setValue("hasRetail", false);
                form.setValue("retailPercentage", undefined);
              }
            }}
            options={BUSINESS_MODEL_OPTIONS}
          />
        ) : (
          <RhfRadioField
            form={form}
            name="hasRetail"
            label={intl.formatMessage(MESSAGES.hasRetailLabel)}
            options={[
              {
                value: false,
                label: intl.formatMessage(COMMON_MESSAGES.noLabel),
                checked: initialValues ? !initialValues.hasRetail : false,
              },
              {
                value: true,
                label: intl.formatMessage(COMMON_MESSAGES.yesLabel),
                checked: initialValues ? initialValues.hasRetail : false,
              },
            ]}
          />
        )}
        {hasRetail && (
          <RhfDropdownField
            form={form}
            name="retailPercentage"
            label={intl.formatMessage(MESSAGES.percentageOfflineLabel)}
            placeholder={intl.formatMessage(COMMON_MESSAGES.select)}
            options={PERCENTAGE_OPTIONS}
            initialValue={initialValues?.retailPercentage}
          />
        )}
        {(!isRssApplicationFlowEnabled || businessModel === "ecommerce_dtc_or_wholesale") && (
          <RhfRadioField
            form={form}
            name="hasDropship"
            label={intl.formatMessage(MESSAGES.hasDropshipLabel)}
            options={[
              {
                value: false,
                label: intl.formatMessage(COMMON_MESSAGES.noLabel),
                checked: initialValues ? !initialValues.hasDropship : false,
              },
              {
                value: true,
                label: intl.formatMessage(COMMON_MESSAGES.yesLabel),
                checked: initialValues ? initialValues.hasDropship : false,
              },
            ]}
          />
        )}
        {hasDropship && (
          <RhfDropdownField
            form={form}
            name="dropshipPercentage"
            label={intl.formatMessage(MESSAGES.percentageDropshipLabel)}
            placeholder={intl.formatMessage(COMMON_MESSAGES.select)}
            options={PERCENTAGE_OPTIONS}
            initialValue={initialValues?.dropshipPercentage}
          />
        )}
      </FormLayout>
    </form>
  );
};

const mapBusinessModelToBusinessModelChoice = (
  businessModel: FundingApplicationSchemas.FundingApplicationBusinessModel
): BusinessModelChoice | null => {
  if (businessModel === "ecommerce_dtc" || businessModel === "wholesale") {
    return "ecommerce_dtc_or_wholesale";
  }

  return businessModel;
};

export interface RevenueFormProps {
  onContinue: (data: FundingApplicationSchemas.CustomerFundingApplicationScreen) => void;
  formId: string;
  onIsSubmittingChange?: (isSubmitting: boolean) => void;
}

export const RevenueForm = ({ onContinue, formId, onIsSubmittingChange }: RevenueFormProps) => {
  const { openErrorModal } = useErrorModal();
  const {
    data: { customerDomain, countryOfIncorporation },
  } = useCustomerAppConfig();
  const { data, isFetched } = useFundingApplication(customerDomain);
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const isRssApplicationFlowEnabled = countryOfIncorporation ? RSS_GEOS.includes(countryOfIncorporation) : false;

  useEffect(() => {
    const isSubmitting = fundingApplicationMutation.isPending;
    onIsSubmittingChange?.(isSubmitting);
  }, [fundingApplicationMutation.isPending, onIsSubmittingChange]);

  const onRevenueSubmit = async (formData: RevenueFormFields) => {
    if (!isFetched || !data) {
      return;
    }
    if (isRssApplicationFlowEnabled && formData.businessModel) {
      addAction("submit_revenue_form", {
        countryOfIncorporation,
        hasRetail: formData.hasRetail,
        businessModel: formData.businessModel,
        revenueCurrency: formData.reportedRevenueCurrency,
        revenueAmount: formData.reportedRevenue,
      });
    }

    await createFundingApplicationMutation(formData)
      .then((fundingApplicationResult) => onContinue(fundingApplicationResult))
      .catch((error) => openErrorModal({ variant: "submit-error", error }));
  };

  const createFundingApplicationMutation = (formData: RevenueFormFields) => {
    return fundingApplicationMutation.mutateAsync({
      ...formData,
      reportedRevenueCurrency: formData.reportedRevenueCurrency
        ? (formData.reportedRevenueCurrency as FundingApplicationSchemas.Currency)
        : undefined,
      retailPercentage: formData.retailPercentage
        ? (formData.retailPercentage as FundingApplicationSchemas.PercentageRanges)
        : undefined,
      dropshipPercentage: formData.dropshipPercentage
        ? (formData.dropshipPercentage as FundingApplicationSchemas.PercentageRanges)
        : undefined,
      id: data.id,
    });
  };

  return isFetched ? (
    <RevenueEntryForm
      onSubmit={onRevenueSubmit}
      isRssApplicationFlowEnabled={isRssApplicationFlowEnabled}
      initialValues={{
        reportedRevenue: data?.reportedRevenue || 0,
        reportedRevenueCurrency: data?.reportedRevenueCurrency || "USD",
        hasRetail: data?.hasRetail || false,
        businessModel: data?.businessModel ? mapBusinessModelToBusinessModelChoice(data?.businessModel) : null,
        // the backend schema returns `null` for retailPercentage
        // however the yup schema adds this field as required when
        // hasRetail is true. The schema validation will fail if we pass null
        // as the default value for this field, at the moment hasRetail is turned on.
        retailPercentage: data?.retailPercentage || undefined,
        hasDropship: data?.hasDropship || false,
        dropshipPercentage: data?.dropshipPercentage || undefined,
      }}
      formId={formId}
    />
  ) : null;
};
